import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs } from './utils'

/**
 * TODO
 *
 * @param todo TODO
 * @example
 * TODO
 * // ->
 * TODO
 */
export interface Exponent<out T = any> {
  /** {@link Exponent} */
  readonly [Key.Exponent]: T extends number
    ? readonly [base: Expression<number>, exponent: Expression<number>]
    : never
}
export namespace Exponent {
  export const isExponent: TypeChecker<Exponent> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.object<Exponent>({
        [Key.Exponent]: CommonArgs.isTwoExpressions as TypeChecker<
          Exponent[Key.Exponent]
        >,
      }),
  ).usingAlias('Fn.Exponent')
  export const assertIsExponent: TypeAsserter<Exponent> =
    TypeAsserter.Factory.from(isExponent)

  /**
   * @see {@link Node}
   */
  export class Node<T extends number = number> extends fnNode.Node<
    T,
    Key.Exponent,
    {
      readonly base: expressionNode.Node
      readonly exponent: expressionNode.Node
    }
  > {
    // NOTE: done as getter in case underlying nodes have getters
    public get type(): common.value.types.Type {
      return (
        [this.arg.base, this.arg.exponent].find(node =>
          common.value.types.Intrinsic.ForNumber.isForNumber(node.type),
        )?.type ?? common.value.types.Intrinsic.Decimal
      )
    }

    constructor(arg: Node<T>['arg']) {
      super(Key.Exponent, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<Exponent> = {
    createNode: (fn, context) => {
      const [baseExpression, exponentExpression] = fn[Key.Exponent]
      const base = expressionNode.from(baseExpression, context)
      const exponent = expressionNode.from(exponentExpression, context)
      if (expressionNode.ForValue.isForValue(base)) {
        const baseValue = Number(base.value)
        if (baseValue === 1) {
          // If the base is 1, the result is always 1 (i.e. 1 ^ X = 1)
          return new expressionNode.ForValue(baseValue)
          // NOTE: the same is almost true for 0, except 0 ^ 0 = 1
        } else if (expressionNode.ForValue.isForValue(exponent)) {
          const exponentValue = Number(exponent.value)
          // If base and exponents are both values, we can calculate the
          // result
          return new expressionNode.ForValue(baseValue ** exponentValue)
        }
      } else if (expressionNode.ForValue.isForValue(exponent)) {
        switch (Number(exponent.value)) {
          case 0:
            // If the exponent is 0, the result is always 1 (i.e. X ^ 0 = 1)
            return new expressionNode.ForValue(1)
          case 1:
            // If the exponent is 1, the result is just the base (i.e. X ^ 1 = X)
            return base
        }
      }
      return new Node({ base, exponent })
    },
    expressionsInArgs: fn => fn[Key.Exponent],
    typeChecker: isExponent,
  }
}
